import { etpPrice, MONTHS } from 'components/stats/agencyGoals';
import { CLIENT_SIZES } from 'constants/clientSizes';
import { AGENCY_TERRITORIES, territoryOf } from 'constants/territories';
import ApiDatabase from 'server';
import { sizedCrm, withCrmFields } from './crmCompanies';
import { LATENCY, dbOf, later, meOf, realCompanies, write } from './crmStore';

/**
 * The yearly plan of the agency, as the « Plan d'action » of the « Tableau
 * pilotage » sets it: an overall revenue for the year, then for each
 * territory (Martinique, Guadeloupe, La Réunion, Saint-Martin), each client
 * size and each month, the new clients to sign. An ETP is worth the SMIC of a
 * month (151,67 h, the SMIC of admin/getMinimumWage) × the average billing
 * coefficient; a client starts at `start` ETP the month it signs, grows by
 * `growth` % a month, up to `max` (components/stats/agencyGoals.js counts it).
 * The plan uses up the overall goal down to 0. They answer from the demo
 * database until the api has them:
 *
 *   getAgencyGoals          POST admin/crm/agency-goals           { year }
 *     → { year, global, coefficient,
 *         sizes: { [size]: { start, growth, max } },
 *         plan: { [territory]: { [size]: [12 counts, January first] } },
 *         territories: [names] }
 *   saveAgencyGoals         POST admin/crm/agency-goals/save      { year, global, coefficient, sizes, plan }
 *   getAgencyGoalsProgress  POST admin/crm/agency-goals/progress  { year }
 *     → { [territory]: { [size]: { clients, revenue } } }: the companies of the
 *       territory turned client during the year, by their size, and the revenue
 *       they made since (invoices, or an estimate from the timesheets before
 *       the first invoice; the demo estimates it: ETP × the price of an ETP ×
 *       months).
 *
 * `territories`: those of the agency (constants/territories), with any that
 * already has a plan; a company counts in the territory of its department.
 * Only the agency manager (500) reads and sets them.
 */

// The « Tableau pilotage »: 1 ETP for a key account or an SME the month it
// signs (0,1 for a very small business), +10 % a month; 1,65 makes an ETP
// worth about 3 000 € a month at the SMIC.
const DEFAULTS = {
  global: 0,
  coefficient: 1.65,
  sizes: {
    grand_compte: { start: 1, growth: 10, max: 5 },
    pme: { start: 1, growth: 10, max: 3 },
    tpe: { start: 0.1, growth: 10, max: 1 },
  },
  plan: {},
};

const goalsOf = (db, year) => ({
  year,
  ...DEFAULTS,
  ...db.agencyGoals?.[year],
});

// The SMIC an hour, gross, as Jobbiz knows it.
const smic = () =>
  new Promise((resolve, reject) =>
    ApiDatabase.getMinimumWage(
      (data) => resolve(Number(data?.hourly?.gross)),
      reject,
    ),
  );

// The real companies with the demo's CRM fields, as the companies list has them.
const companiesOf = (payload) => {
  const db = dbOf(payload);
  return (
    payload?.scenario === 'vide' ? Promise.resolve([]) : realCompanies({})
  ).then((list) =>
    withCrmFields(
      list.filter((company) => !db.deletedCompanies?.[String(company._id)]),
      {
        meId: meOf(payload).id,
        team: db.team,
        now: new Date(),
        overrides: db.companyCrm,
      },
    ).map((company) => ({ ...company, ...sizedCrm(company, db) })),
  );
};

// A demo read built on a promise: `chargement` never answers, `erreur` fails.
const answer = (payload, resolve, reject, build) => {
  if (payload?.scenario === 'chargement') return;
  if (payload?.scenario === 'erreur') {
    later(LATENCY.read, () => reject(new Error('Mock: the CRM api failed')));
    return;
  }
  build(dbOf(payload)).then(
    (result) => later(LATENCY.read, () => resolve(result)),
    reject,
  );
};

const isPositive = (value) =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0;
const isCount = (value) => Number.isInteger(value) && value >= 0;

const crmGoalsApi = {
  getAgencyGoals: (payload, resolve, reject) =>
    answer(payload, resolve, reject, (db) => {
      const goals = goalsOf(db, payload.year);
      const territories = [
        ...new Set([...AGENCY_TERRITORIES, ...Object.keys(goals.plan)]),
      ].sort((a, b) => a.localeCompare(b, 'fr'));
      return Promise.resolve(
        JSON.parse(JSON.stringify({ ...goals, territories })),
      );
    }),

  saveAgencyGoals: (payload, resolve, reject) =>
    write(payload, resolve, reject, (db) => {
      const { year, global, coefficient, sizes, plan } = payload;
      if (!Number.isInteger(year))
        throw new Error('L’année des objectifs manque.');
      if (!isPositive(global) || !isPositive(coefficient))
        throw new Error('Un montant est un nombre positif.');
      if (
        !CLIENT_SIZES.every((size) =>
          ['start', 'growth', 'max'].every((key) =>
            isPositive(sizes?.[size]?.[key]),
          ),
        )
      )
        throw new Error('Les ETP d’un client sont des nombres positifs.');
      if (
        !Object.values(plan ?? {}).every((row) =>
          CLIENT_SIZES.every(
            (size) =>
              (row[size] ?? []).length <= MONTHS &&
              (row[size] ?? []).every(isCount),
          ),
        )
      )
        throw new Error('Un nombre de clients est un entier positif.');
      db.agencyGoals = {
        ...db.agencyGoals,
        [year]: { global, coefficient, sizes, plan },
      };
      return goalsOf(db, year);
    }),

  getAgencyGoalsProgress: (payload, resolve, reject) =>
    answer(payload, resolve, reject, (db) =>
      Promise.all([companiesOf(payload), smic()]).then(
        ([companies, hourly]) => {
          const price = etpPrice(
            hourly || 0,
            goalsOf(db, payload.year).coefficient,
          );
          const now = new Date();
          const end = new Date(payload.year + 1, 0, 1);
          const until = now < end ? now : end;
          const progress = {};
          companies
            .filter(
              (company) =>
                company.stage === 'client' &&
                territoryOf(company) &&
                company.size &&
                Number(String(company.clientSince).slice(0, 4)) ===
                  payload.year,
            )
            .forEach((company) => {
              const months = Math.max(
                0,
                (until - new Date(company.clientSince)) / (30.44 * 86400000),
              );
              const territory = territoryOf(company);
              const row = (progress[territory] = progress[territory] ?? {});
              const cell = (row[company.size] = row[company.size] ?? {
                clients: 0,
                revenue: 0,
              });
              cell.clients += 1;
              cell.revenue += Math.round((company.etp ?? 0) * price * months);
            });
          return progress;
        },
      ),
    ),
};

export default crmGoalsApi;
